import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { useHistory } from 'react-router';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useUserQueries from '@dashboard/hooks/queries/useUserQueries';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import AuthLayout from '@dashboard/layouts/AuthLayout';
import storage from '@dashboard/services/storage';

import Button from '@dashboard/components/Button';
import { FormElement } from '@dashboard/components/Form';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Form from '@dashboard/components/Formik/Form';
import Link from '@dashboard/components/Link';
import Meta from '@dashboard/components/Meta';
import { GoogleSignIn } from '@dashboard/components/GoogleSignIn';

import { signup } from './Signup/helpers';

export default function AcceptInvite() {
  const { setLogged } = useAuth();
  const history = useHistory();
  const { invite, team, is_existing_user } = useUrlQuery();
  const { acceptInvite } = useUserQueries();
  const [error, setError] = React.useState(null);

  const acceptInviteMutation = useMutation({
    mutationFn: () =>
      acceptInvite({
        invite,
        session: storage.session.getItem('CheckoutPage.sessionId'),
      }),
    onSuccess: () => {
      window.alert('Invite accepted! Please login to continue.');
      history.push('/login');
    },
    onError: error => {
      setError(error.message);
    },
  });

  return (
    <>
      <Meta
        meta={{
          title: 'Accept invite',
        }}
      />

      <AuthLayout>
        <div className="text-center">
          <h1 className="text-4xl">Join the {team} team on Checkout Page</h1>
        </div>

        <div className="py-4">
          {/* Google Sign-In */}
          <div className="mb-6">
            <GoogleSignIn 
              mode="invite"
              onSuccess={(result) => {
                console.log('Google invite acceptance success:', result);
                if (result.inviteAccepted) {
                  window.alert('Invite accepted! Welcome to the team.');
                  history.push('/dashboard');
                } else {
                  window.alert('Invite accepted! Please complete your profile.');
                  history.push('/dashboard');
                }
              }}
              onError={(error) => {
                console.error('Google invite acceptance error:', error);
                setError(error.message);
              }}
            />
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with email</span>
            </div>
          </div>

          {is_existing_user ? (
            <>
              <h3 className="text-lg mt-2 font-normal mb-4">
                You already have an account. Accept the invite to join the team.
              </h3>

              <FormElement>
                <FormErrorBase>{error}</FormErrorBase>
              </FormElement>

              <Button
                onClick={() => acceptInviteMutation.mutate()}
                size="full"
                appearance="primary"
              >
                Accept invite
              </Button>
            </>
          ) : (
            <Form
              initialValues={{
                name: '',
                password: '',
                marketingConsent: true,
              }}
              onSubmit={async (values, actions) => {
                const response = await signup({
                  ...values,
                  session: storage.session.getItem('CheckoutPage.sessionId'),
                  invite,
                });

                actions.resetForm();

                return {
                  data: {
                    name: response.data.user.name,
                    email: response.data.user.email,
                  },
                  error: response.error || response.message,
                  onSuccess: () => {
                    setLogged({
                      user: response.data.user,
                      seller: response.data.user.seller,
                      token: response.data.token,
                    });

                    history.push('/dashboard');
                  },
                };
              }}
              showState={false}
              fields={[
                {
                  name: 'name',
                  label: 'First name',
                  placeholder: 'First name',
                  required: true,
                },
                {
                  name: 'password',
                  label: 'Password',
                  placeholder: 'Password',
                  type: 'password',
                  required: true,
                },
              ]}
              submitButton={{
                label: 'Create account',
                appearance: 'primary',
                size: 'full',
              }}
            />
          )}
        </div>

        <div className="text-center">
          Already have an account?{' '}
          <Link href="/login" variant="secondary">
            Log in
          </Link>
        </div>
      </AuthLayout>
    </>
  );
}
